import { Box, Button, CardActions, ClickAwayListener, Collapse, Divider, TextField } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import makeStyles from '@mui/styles/makeStyles';
import { connect } from "react-redux";
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Comment from "./Comment"
import { selectProject, selectUserId } from "../../redux/selectors/editor";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import { tryParseJSON } from "../../utility/json";
import { CommentReplyDetails } from "../notifications/CommentReplyNotification";

export interface IRange
{
    start: number, end: number
}

export interface ISheetComment
{
    name: "sheet",
    sheetId: string,
    row: string,
    column: string
}

export interface IScriptComment
{
    name: "script",
    state: string,
    range: IRange
}

export interface IMultiStateComment
{
    name: "multistate",
    state: string[]
}

export type CommentType = IScriptComment | IMultiStateComment | ISheetComment;

export interface IComment
{
    userId: string
    timestamp: number
    comment: string
    deleted?: boolean
}

export type Comments = { [commentId: string]: ICommentContainer };

export interface ICommentContainer extends IComment
{
    type: CommentType
    replies: { [commentId: string]: IComment }
    resolved?: boolean
}

const width = 300;
const useStyles = makeStyles(theme => ({
    comment: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
        width: width,
        zIndex: 1
    },
    selected: {
        cursor: "pointer"
    },
    replyContainer: {
        display: 'flex',
        gridGap: "4px",
        flexWrap: "wrap",
        width: "100%",
        marginTop: theme.spacing(1)
    },
    replyActions: {
        display: 'flex',
        gridGap: "4px"
    }
}));

type Expanded = { [commentId: string]: boolean };

interface IProps
{
    commentId: string
    details: ICommentContainer
    highlighted?: boolean
    raised?: boolean
    project: string
    userId: string | null
    stateClasses?: {
        highlighted?: string,
        raised?: string,
        default?: string
    }
    defaultSelected?: boolean
    repliesCollapsible?: boolean
    component: React.FunctionComponent<any>
    onHighlightComment?: (commentID?: string) => void
}

function CommentContainer({ component, repliesCollapsible, commentId, details, highlighted, defaultSelected, onHighlightComment, project, raised, userId, stateClasses }:
    IProps)
{
    const expandedLocalStorage = `commentExpanded/${project}`;
    const classes = useStyles();
    const [selected, setSelected] = useState(defaultSelected === true);
    const [replyFocus, setReplyFocus] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [containerHighlight, setContainerHighlight] = useState(highlighted);
    const [classNames, setClassNames] = useState<string[]>([]);
    const { pushCommentReply, setCommentMember, setCommentReplyMember, pushNotification } = useContext(FirebaseContext);

    const getExpandedStates = useCallback(() => tryParseJSON<Expanded>(localStorage.getItem(expandedLocalStorage), {}), [expandedLocalStorage]);

    const [expanded, setExpanded] = useState(getExpandedStates()[commentId] === true || !repliesCollapsible);

    const handlePointerEnter = useCallback(() =>
    {
        if (!containerHighlight)
        {
            onHighlightComment && onHighlightComment(commentId);
        }
        setContainerHighlight(true);
    }, [commentId, containerHighlight, onHighlightComment, setContainerHighlight]);
    const handlePointerLeave = useCallback(() =>
    {
        setContainerHighlight(false);
        onHighlightComment && onHighlightComment(undefined);
    }, [onHighlightComment, setContainerHighlight]);

    const handleOnClickCancel = () =>
    {
        if (selected)
        {
            setSelected(false);
            setReplyText("");
            setReplyFocus(false);
        }
    }

    const handleOnClick = () =>
    {
        if (!selected)
            setSelected(true);
    }

    const handleOnClickReply = () =>
    {
        if (replyText.length > 0 && userId)
        {
            const timestamp = Date.now();
            pushCommentReply(project, commentId, { userId, timestamp, comment: replyText }).then(() =>
            {
                if (details.userId !== userId)
                {
                    const notificationDetail: CommentReplyDetails = {
                        type: "commentReply",
                        postedByUserId: userId,
                        commentId,
                        project
                    }
                    pushNotification(details.userId, notificationDetail);
                }
            });

            setReplyText("");
            setReplyFocus(false);
        }
    }

    const handleOnChangeReply = (event: ChangeEvent<HTMLInputElement>) =>
    {
        setReplyText(event.currentTarget.value);
    }

    const handleOnFocusReply = () => 
    {
        setReplyFocus(true);
    }

    const updateComment = (newComment: string) =>
    {
        setCommentMember(project, commentId, "comment", newComment);
    }

    const updateCommentReply = (replyId: string) =>
    {
        return (newCommentReply: string) =>
        {
            setCommentReplyMember(project, commentId, replyId, project, newCommentReply);
        }
    }

    const deleteComment = () => setCommentMember(project, commentId, "deleted", true);

    const deleteCommentReply = (replyId: string) =>
    {
        return () => setCommentReplyMember(project, commentId, replyId, "deleted", true);
    };

    const handleClickExpand = () =>
    {
        setExpanded(!expanded);
    }

    useEffect(() =>
    {
        const classNames = [classes.comment];

        if (!selected)
        {
            classNames.push(classes.selected);
        }

        if (containerHighlight)
        {
            if (stateClasses?.highlighted !== undefined)
            {
                classNames.push(stateClasses.highlighted);
            }
        }
        else if (raised)
        {
            if (stateClasses?.raised !== undefined)
            {
                classNames.push(stateClasses.raised);
            }
        }
        else
        {
            if (stateClasses?.default !== undefined)
            {
                classNames.push(stateClasses.default);
            }
        }
        setClassNames(classNames);
    }, [classes, containerHighlight, selected, setClassNames, raised, stateClasses]);

    const replies = useMemo(() =>
    {
        return details.replies ? Object.entries(details.replies).filter(([, reply]) =>
        {
            return !reply?.deleted;
        }) : undefined;
    }, [details.replies])

    useEffect(() =>
    {
        const expandedStates = getExpandedStates();
        expandedStates[commentId] = expanded;
        localStorage.setItem(expandedLocalStorage, JSON.stringify(expandedStates));
    }, [expanded, expandedLocalStorage, commentId, getExpandedStates])

    return (
        <ClickAwayListener onClickAway={handleOnClickCancel}>
            {React.createElement(component, {
                className: classNames.join(" "),
                onClick: handleOnClick, onPointerOver: handlePointerEnter, onPointerLeave: handlePointerLeave,
                children: <>
                    <Comment details={details} updateComment={updateComment}
                        deleteComment={deleteComment} deleteText="Delete this comment thread?"
                        markAsResolved={() => { setCommentMember(project, commentId, "resolved", true); }} />

                    {replies && replies.length > 0 && <>
                        {repliesCollapsible && <Button onClick={handleClickExpand} size="small" variant={"text"} startIcon={expanded ? <ExpandLess /> : <ExpandMore />}>
                            {expanded ? "Hide Replies" : `Show ${replies.length} replies`}
                        </Button>}
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            {replies.map(([id, reply]) =>
                            {
                                return <Box key={id}>
                                    <Divider />
                                    <Comment details={reply} updateComment={updateCommentReply(id)} deleteText="Delete this reply?" deleteComment={deleteCommentReply(id)} />
                                </Box>
                            })}
                        </Collapse>
                    </>}

                    {selected && <><Divider /><CardActions disableSpacing>
                        <Box className={classes.replyContainer}>
                            <TextField multiline={true} id="comment" variant={"outlined"}
                                size={"small"} placeholder={"Enter a reply..."}
                                onChange={handleOnChangeReply} onFocus={handleOnFocusReply}
                                value={replyText} fullWidth />
                            {replyFocus && <Box className={classes.replyActions}>
                                <Box><Button variant="contained" size={"small"} color={"primary"} onClick={handleOnClickReply} disabled={replyText.length === 0}>Reply</Button></Box>
                                <Box><Button variant="contained" size={"small"} color={"primary"} onClick={handleOnClickCancel}>Cancel</Button></Box>
                            </Box>}
                        </Box>
                    </CardActions></>}
                </>
            })}

        </ClickAwayListener >
    );
}

const mapStateToProps = (store: never) => ({
    project: selectProject(store),
    userId: selectUserId(store),
});

export default connect(mapStateToProps, {
})(CommentContainer);